import { resolveTaskName } from '../utils/utils';

/** Shown when neither the task nor any ancestor has its own UOM. Not a saved value. */
export const DISPLAY_UOM_FALLBACK = '%';

export interface EffectiveUomSource {
  taskId: string;
  taskName: string;
}

export interface EffectiveUom {
  /** Unit to show. `%` when nothing in the ancestry has a UOM. */
  uom: string;
  /** Set when the unit comes from an ancestor, not this task. */
  inheritedFrom: EffectiveUomSource | null;
  /** True when `uom` is the display fallback, not a stored value. */
  isDisplayFallback: boolean;
}

type UomTask = {
  id: string;
  parentTaskId?: string | null;
  uom?: string | null;
  taskName?: string | null;
};

function ownUom(task: UomTask): string | null {
  if (typeof task.uom !== 'string') return null;
  const trimmed = task.uom.trim();
  return trimmed ? trimmed : null;
}

/**
 * Own UOM, else the nearest ancestor's. `%` is only the display fallback.
 * A missing task or a parent cycle stops the walk.
 */
export function effectiveUom(
  taskId: string,
  getTask: (id: string) => UomTask | null | undefined,
): EffectiveUom {
  const fallback: EffectiveUom = {
    uom: DISPLAY_UOM_FALLBACK,
    inheritedFrom: null,
    isDisplayFallback: true,
  };
  const visited = new Set<string>();
  let currentId: string | null = taskId;

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const task = getTask(currentId);
    if (!task) return fallback;
    const uom = ownUom(task);
    if (uom) {
      const inherited = currentId !== taskId;
      const taskName = resolveTaskName(task);
      return {
        uom,
        inheritedFrom: inherited
          ? { taskId: currentId, taskName: taskName || 'parent' }
          : null,
        isDisplayFallback: false,
      };
    }
    currentId = task.parentTaskId ?? null;
  }

  return fallback;
}

/** `m3 (from Tower A)` when the unit is inherited. Null for an own unit or the `%` fallback. */
export function formatInheritedUomLabel(result: EffectiveUom): string | null {
  if (!result.inheritedFrom || result.isDisplayFallback) return null;
  return `${result.uom} (from ${result.inheritedFrom.taskName})`;
}
